import dayjs from "dayjs"
import { TFunction } from "react-i18next"
import { SectionScheduldeQuestionnaire } from "../../../../../service/schedule/response"
import { AssignScheduleDetail } from "../../../../../service/userSchedule/response"
import { Constants } from "../../../../../utils/constants"
import { NavigatioStateType } from "../../../../../utils/enums/navigatetionState"
import { QuestionOptionType } from "../../../../../utils/enums/optionType"
import { TypeOfQuestionarie } from "../../../../../utils/enums/typeQuestionarie"
import { deleteDecimalsWithZero, oneHundredAsPercentage } from "../../../../../utils/methods/removeDecimals"
import { AnswerProps } from "../../../../calendar/pages/detail/components/answer/types"
import { BodySectionOmitData } from "../../../../calendar/pages/detail/components/bodySection/types"
import { QuestionProps } from "../../../../calendar/pages/detail/components/question/types"
import { SectionProps } from "../../../../calendar/pages/detail/components/section/types"
import { VisitData } from "../../../../calendar/pages/detail/components/visite/type"
import { VisitListData } from "../../../../calendar/pages/detail/components/visitList/types"

const formatResponse = ({ response, t }: { response: AssignScheduleDetail; t: TFunction<"translation", undefined> }) => {
    // const store = response.store
    const management = response.userAssign
    // const supervisor = response.userManage
    const creator = response.createdByUser
    const percentage: string[] | undefined = response?.resultQuestionnaire?.split?.('/')
    const resultQuestionnaire = percentage ? (parseInt(percentage?.[0]) / parseInt(percentage?.[1])) * 100 : null
    const questionnaireMain = response?.questionnaireMain
    const isScheduleOpen = Boolean(Number(response?.openScheduled ?? 0))
    const dateStart = response?.dateStart ? dayjs(response?.dateStart).locale('es').format(Constants.dayjsFormat4) : ''
    const dateEnd = response?.dateEnd ? dayjs(response?.dateEnd).locale('es').format(Constants.dayjsFormat4) : ''
    const visitList: VisitListData = {
        from: response?.dateStart ? dayjs(response.dateStart).locale('es').format(Constants.dayjsFormat4) : 'N/A',
        to: response.dateEnd ? dayjs(response.dateEnd).locale('es').format(Constants.dayjsFormat4) : 'N/A',
        reportUrl: response.reportUrl,
        visitList: [{
            id: response.id?.toString(),
            title: questionnaireMain?.name,
            status: response?.statusId,
            result: resultQuestionnaire || resultQuestionnaire == 0 ? `${resultQuestionnaire.toFixed(2)}%` : 'Sin resultado',
            sectionCompleted: `${response.amountSectionsCompleted}/${questionnaireMain?.sectionCount}`,
            questonCompleted: `${response.amountQuestionsAnswered}/${questionnaireMain?.sumQuestions}`,
            programedType: t(`schedule.scheduleTypes.${isScheduleOpen ? 'free' : 'programmed'}`),
            dateEnd,
            dateStart,
            detailVersion: {
                parentId: questionnaireMain?.parentId || questionnaireMain?.id,
                version: questionnaireMain?.version || '1'
            },
            sectonList: [],
            ...response.reportResultDate && {
                infoReport: {
                    date: dayjs(response.reportResultDate).locale('es').format(Constants.dayjsFormat4),
                    url: response?.reportResultUrl
                },
            },
            ...response.reportDate && {
                report: {
                    date: dayjs(response.reportDate).locale('es').format(Constants.dayjsFormat4),
                    url: response?.reportUrl
                },
            },
            ...response.lastDateReportFile && {
                lastReport: {
                    date: dayjs(response.lastDateReportFile).locale('es').format(Constants.dayjsFormat4),
                    url: response?.lastReportFile
                },
            }
        }],
    }
    const store = response.store
    const bodySection: BodySectionOmitData = {
        ...!!store && {
            store: {
                title: `${store?.name} - ${store?.keyCode}`,
                timeZone: store.storeTimeZone?.zone,
                description: `${dayjs(store?.updateTime).locale('es').format(Constants.dayjsFormat4)}, ${store?.address} ${store?.email ?? ''} ${store?.phone && store?.phone.length > 1 ? `/ ${store?.phone}` : ''}`
            }
        },
        management: {
            title: `${management?.firstName} ${management?.lastName}`,
            userType: `${management?.userType?.name}`,
            curp: `${management?.curp}`,
            role: `${management?.role?.description}`,
            email: `${management?.email}`,
            phone: `${management?.phone}`
        },
        directManager: response?.directParent?.map(parent => {
            return {
                id: parent.id?.toString(),
                title: `${parent.firstName} ${parent.lastName}`,
                userType: `${parent.userType}`,
                curp: `${parent.curp}`,
                role: `${parent.role}`,
                email: `${parent.email}`,
                phone: `${parent.phone}`
            }
        }) || [],
        createrUser: {
            title: `${creator?.firstName} ${creator?.lastName}`,
            userType: `${creator?.userType?.name}`,
            curp: `${creator?.curp}`,
            role: `${creator?.role?.description}`,
            email: `${creator?.email}`,
            phone: `${creator?.phone}`
        },
        supervisor: [],
        reportData: {
            report: {
                date: visitList.from,
                url: visitList.reportUrl
            }
        },
        status: response?.statusId,
        typeOfQuestionnaire: TypeOfQuestionarie.BYUSER
    }
    return {
        visitList,
        bodySection
    }
}

const formatScheduleByQuestionnaireResponse = ({ response, header }: { response: SectionScheduldeQuestionnaire[]; header: { title: string } & VisitData['detailVersion'] }): SectionProps[] => {
    const sectionList: SectionProps[] = response?.map((section, index) => {
        return {
            amountQuestions: section.amountQuestions ?? 0,
            description: section.description ?? '',
            statusId: section.statusId ?? 0,
            index,
            pointsOfUser: deleteDecimalsWithZero((section.pointsOfUser ?? 0).toFixed(2)),
            title: section.title ?? '',
            totalPoints: deleteDecimalsWithZero((section.totalPoints ?? 0).toFixed(2)),
            value: oneHundredAsPercentage( deleteDecimalsWithZero( (((section.pointsOfUser || 0) / (section.totalPoints || 1)) * 100).toFixed(2) ) ).toString(),
            questions: (section?.questions?.map((question, index) => {
                const questionFormat: QuestionProps = {
                    index: [index],
                    haveConfigMultiple: {
                        actionPlan: false,
                        evidence: false,
                        reportFolio: false,
                        residenceAlert: false,
                    },
                    position: '1',
                    title: question.title ?? '',
                    type: question.questionTypeId as QuestionOptionType,
                    description: (question.description ?? ''),
                    textHelp: question.textHelp ?? '',
                    evidenceQuestion: question.evidences ?? [],
                    evidenceMultiple: [{}],
                    answers: (question?.answers?.map((answer, index) => {
                        const answerFormat: Omit<AnswerProps, "type"> & { id: string; } = ({
                            correctOption: !!answer.correctOption,
                            navigateType: NavigatioStateType.USER_SCHEDULE,
                            haveConfig: {
                                actionPlan: !!answer?.actionPlanImplementedId,
                                evidence: !!answer?.evidences?.length,
                                reportFolio: !!answer?.reportFoliosIds,
                                residenceAlert: !!answer?.recurrence,
                            },
                            ids: {
                                actionPlan: answer?.actionPlanImplementedId ?? '',
                                reportFolio: answer?.reportFoliosIds ?? '',
                                residenceAlert: answer?.recurrence ?? '',
                            },
                            header: {
                                ...header ?? {},
                                title: header.title,
                                item: answer.description ?? '',
                                question: question.title ?? ''
                            },
                            id: '1',
                            index: [index],
                            score: answer.score ?? '0',
                            title: answer.description ?? '',
                            action: { label: '', value: '' },
                            evidence: answer.evidences ?? [],
                            iconUrl: answer.iconUrl ?? '',
                            selected: !!answer?.selected,
                        })
                        return answerFormat
                    }) || [])
                }
                return questionFormat
            }) || []),
        }
    })
    return sectionList
}

export { formatResponse, formatScheduleByQuestionnaireResponse }
